import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { AnimatedArrow } from '../components/AnimatedArrow';
import { ParallaxShapes } from '../components/ParallaxShapes';
import { useSEO } from '../hooks/useSEO';
import { ProjectsGridSkeleton } from '../components/Skeleton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('Alle');
  const [scrollY, setScrollY] = useState(0);

  const filters = ['Alle', 'In Entwicklung', 'Realisiert', 'Verkauft'];

  useEffect(() => {
    fetchProjects();
    
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const fetchProjects = async () => {
    try {
      // Use public endpoint to get only visible projects
      const response = await fetch(`${API_URL}/api/projects/public`);
      if (response.ok) {
        const data = await response.json();
        setProjects(data);
      }
    } catch (err) {
      console.error('Error fetching projects:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredProjects = filter === 'Alle' 
    ? projects 
    : projects.filter(project => project.status === filter);

  useSEO('projects');

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-gray-50 via-white to-gray-100">
      {/* Parallax Shapes Background */}
      <ParallaxShapes scrollY={scrollY} />
      
      {/* Content Container */}
      <div className="relative" style={{ zIndex: 2 }}>
        {/* Hero Section */}
        <section className="pt-32 pb-20 bg-[#264F77] text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-5xl md:text-6xl font-normal mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                Unsere Projekte
              </h1>
              <p className="text-xl text-gray-200 leading-relaxed">
                Entdecken Sie einen Auszug aus unseren aktuellen Entwicklungen und realisierten Projekten in der Ostschweiz. Jedes Projekt vereint architektonische Qualität mit nachhaltiger Wertschöpfung.
              </p>
            </div>
          </div>
        </section>

        {/* Filter Section */}
        <section className="py-8 bg-white/90 backdrop-blur-sm border-b border-gray-200 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-3">
            {filters.map((filterOption) => (
              <button
                key={filterOption}
                onClick={() => setFilter(filterOption)}
                className={`px-6 py-2 rounded-full font-medium transition-all duration-300 ${
                  filter === filterOption
                    ? 'bg-[#264F77] text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {filterOption}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-20 bg-white/70 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <ProjectsGridSkeleton count={6} />
          ) : filteredProjects.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-gray-600 text-lg">
                Keine Projekte in dieser Kategorie gefunden.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
              {filteredProjects.map((project, index) => (
                <Link
                  to={`/projekte/${project.id}`}
                  key={project.id}
                  className="group block bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300"
                >
                  <div className="relative h-80 overflow-hidden image-zoom-container">
                    <img
                      src={project.image}
                      alt={`${project.title} - ${project.type || 'Immobilie'} in ${project.location} | Haus kaufen ${project.location}`}
                      title={`${project.title} - ${project.status} | LIVAG Immobilienprojekt in ${project.location}`}
                      loading="lazy"
                      width="400"
                      height="320"
                      className="w-full h-full object-cover image-zoom"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                    <div className="absolute top-6 right-6">
                      <span className={`px-4 py-2 text-white text-xs font-semibold rounded-full shadow-lg ${
                        project.status === 'In Entwicklung' ? 'bg-blue-500' :
                        project.status === 'Realisiert' ? 'bg-green-500' :
                        'bg-gray-500'
                      }`}>
                        {project.status}
                      </span>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-8">
                      <h3 className="text-2xl font-bold text-white mb-2">
                        {project.title}
                      </h3>
                      <p className="text-gray-200 text-sm mb-4">{project.location}</p>
                    </div>
                  </div>
                  <div className="p-8 bg-white group-hover:bg-gray-50 transition-colors duration-300">
                    <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
                      <div>
                        <span className="text-gray-500 text-xs uppercase tracking-wide">Typ</span>
                        <p className="font-semibold text-gray-800 mt-1">{project.type}</p>
                      </div>
                      <div>
                        <span className="text-gray-500 text-xs uppercase tracking-wide">Einheiten</span>
                        <p className="font-semibold text-gray-800 mt-1">{project.units}</p>
                      </div>
                    </div>
                    {project.description && (
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2">{project.description}</p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

        {/* CTA Section */}
        <section className="py-24 bg-white/80 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-normal text-[#264F77] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              Interesse an einem Projekt?
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Kontaktieren Sie uns für detaillierte Informationen zu unseren Projekten oder für eine persönliche Beratung.
            </p>
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center px-8 py-4 bg-[#264F77] text-white font-semibold rounded-md hover:bg-[#1e3d5c] transition-colors duration-300"
            >
              Kostenloses Erstgespräch vereinbaren
              <ArrowRight className="ml-2" size={20} />
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};
